import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dealerData, subData] = await Promise.all([
        api.getAvailableDealers(),
        api.getSubscriptions()
      ]);
      setDealers(dealerData);
      setSubscriptions(subData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (dealerId) => {
    try {
      await api.subscribe(dealerId);
      await loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleUnsubscribe = async (subscriptionId) => {
    if (!window.confirm('Unsubscribe from this dealer?')) return;
    try {
      await api.unsubscribe(subscriptionId);
      await loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <h2>Welcome, {user?.company_name || 'Partner'}</h2>

      {user?.account_status === 'pending' && (
        <div className="alert alert-warning">
          Your account is pending approval. You cannot subscribe to dealers yet.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <div className="dashboard-grid">
        <div className="card stat-card">
          <div className="number">{subscriptions.length}</div>
          <div className="label">Active Subscriptions</div>
        </div>
        <div className="card stat-card">
          <div className="number">{dealers.length}</div>
          <div className="label">Available Dealers</div>
        </div>
        <div className="card stat-card">
          <div className="number">${user?.credit_balance || 0}</div>
          <div className="label">Credit Balance</div>
        </div>
        <div className="card stat-card">
          <div className="number">{user?.account_status}</div>
          <div className="label">Account Status</div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <h3>Your Subscriptions</h3>
        {subscriptions.length === 0 ? (
          <p>No active subscriptions. Browse dealers below to subscribe.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '10px' }}>Dealer</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Subscribed</th>
                <th style={{ textAlign: 'right', padding: '10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => (
                <tr key={sub.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px' }}>{sub.dealership_name}</td>
                  <td style={{ padding: '10px' }}>
                    {new Date(sub.subscribed_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    <Link to={`/partner/inventory/${sub.dealership_id}`} className="btn btn-primary" style={{ marginRight: '10px' }}>
                      View Inventory
                    </Link>
                    <button onClick={() => handleUnsubscribe(sub.id)} className="btn btn-danger">
                      Unsubscribe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <h3>Available Dealers</h3>
        {dealers.length === 0 ? (
          <p>No dealers available for subscription.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '10px' }}>Dealer</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Location</th>
                <th style={{ textAlign: 'center', padding: '10px' }}>Vehicles</th>
                <th style={{ textAlign: 'right', padding: '10px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {dealers.map(dealer => (
                <tr key={dealer.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px' }}>{dealer.dealership_name}</td>
                  <td style={{ padding: '10px' }}>{dealer.city}, {dealer.state}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{dealer.vehicle_count}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    {dealer.is_subscribed ? (
                      <span className="status status-active">Subscribed</span>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(dealer.id)}
                        className="btn btn-success"
                        disabled={user?.account_status !== 'active'}
                      >
                        Subscribe
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
